/**
 * Shared create/edit form for marketing banner configuration.
 */

import React from 'react';
import { Card, CardBody, CardHeader } from '@/components/common/Card';
import { Input, Select, TextArea } from '@/components/common/FormField';
import { StaticMessagesEditor } from '@/components/marketingBanners/StaticMessagesEditor';
import { TEMPLATE_VARIABLES } from '@/app/types/rewards';
import type {
  CreateMarketingBannerRequest,
  MarketingBannerCtaAction,
  MarketingBannerSourceType,
  MarketingBannerStatus,
  MarketingMessageAnimation,
} from '@/app/types/marketingBanners';
import {
  MARKETING_BANNER_CTA_ACTION_OPTIONS,
  MARKETING_BANNER_SOURCE_OPTIONS,
  MARKETING_BANNER_STATUS_OPTIONS,
  MARKETING_MESSAGE_ANIMATION_OPTIONS,
} from '@/app/types/marketingBanners';

import './MarketingBannerForm.scss';

interface MarketingBannerFormProps {
  value: CreateMarketingBannerRequest;
  onChange: (next: CreateMarketingBannerRequest) => void;
  disabled?: boolean;
}

export const MarketingBannerForm: React.FC<MarketingBannerFormProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const staticContent = value.static_content ?? {};
  const externalApi = value.external_api ?? { endpoint: '' };
  const baseUrl = externalApi.base_url ?? {};
  const httpMethod = (externalApi.method ?? 'GET').toUpperCase();

  const [payloadText, setPayloadText] = React.useState('');
  const [payloadError, setPayloadError] = React.useState<string | null>(null);
  const [headersText, setHeadersText] = React.useState('');
  const [headersError, setHeadersError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (value.source_type !== 'external_api') {
      return;
    }
    const payload = value.external_api?.payload;
    setPayloadText(
      payload && Object.keys(payload).length > 0 ? JSON.stringify(payload, null, 2) : ''
    );
    setPayloadError(null);
    const headers = value.external_api?.headers;
    setHeadersText(
      headers && Object.keys(headers).length > 0 ? JSON.stringify(headers, null, 2) : ''
    );
    setHeadersError(null);
  // Re-sync JSON textareas when switching banners (endpoint), not on every keystroke
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.source_type, value.external_api?.endpoint]);

  const patch = (partial: Partial<CreateMarketingBannerRequest>) => {
    onChange({ ...value, ...partial });
  };

  const patchStatic = (partial: Partial<NonNullable<CreateMarketingBannerRequest['static_content']>>) => {
    onChange({
      ...value,
      static_content: { ...staticContent, ...partial },
    });
  };

  const ctaAction = staticContent.cta_action ?? null;
  const isNavigationCta =
    ctaAction === 'external_browser' ||
    ctaAction === 'rewards_route' ||
    ctaAction === 'cross_mfe';
  const isRedemptionCta = ctaAction === 'start_redemption';

  const patchExternal = (partial: Partial<NonNullable<CreateMarketingBannerRequest['external_api']>>) => {
    onChange({
      ...value,
      external_api: { ...externalApi, ...partial },
    });
  };

  const handleHeadersChange = (text: string) => {
    setHeadersText(text);
    if (!text.trim()) {
      setHeadersError(null);
      patchExternal({ headers: {} });
      return;
    }
    try {
      const parsed = JSON.parse(text) as unknown;
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        setHeadersError(null);
        patchExternal({ headers: parsed as Record<string, string> });
      } else {
        setHeadersError('Headers must be a JSON object');
      }
    } catch {
      setHeadersError('Invalid JSON');
    }
  };

  const handlePayloadChange = (text: string) => {
    setPayloadText(text);
    if (!text.trim()) {
      setPayloadError(null);
      patchExternal({ payload: {} });
      return;
    }
    try {
      const parsed = JSON.parse(text) as unknown;
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        setPayloadError(null);
        patchExternal({ payload: parsed as Record<string, unknown> });
      } else {
        setPayloadError('Payload must be a JSON object');
      }
    } catch {
      setPayloadError('Invalid JSON');
    }
  };

  return (
    <div className="marketing-banner-form">
      <Card>
        <CardHeader title="Banner identity" />
        <CardBody>
          <Input
            label="Name"
            value={value.name}
            onChange={(v) => patch({ name: v })}
            disabled={disabled}
            required
          />
          <Input
            label="Priority"
            type="number"
            value={String(value.priority)}
            onChange={(v) => patch({ priority: Number(v) })}
            disabled={disabled}
            required
            hint="Lower number = higher precedence. Must be unique."
          />
          <Select
            label="Status"
            value={value.status}
            options={MARKETING_BANNER_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            onChange={(v) => patch({ status: v as MarketingBannerStatus })}
            disabled={disabled}
          />
          <Select
            label="Source type"
            value={value.source_type}
            options={MARKETING_BANNER_SOURCE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            onChange={(v) => patch({ source_type: v as MarketingBannerSourceType })}
            disabled={disabled}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Display & CTA (static_content)" />
        <CardBody>
          <Input
            label="Title"
            value={staticContent.title ?? ''}
            onChange={(v) => patchStatic({ title: v })}
            disabled={disabled}
          />
          <Input
            label="Image name"
            value={staticContent.image_name ?? ''}
            onChange={(v) => patchStatic({ image_name: v })}
            disabled={disabled}
            hint="Asset filename served by rewards-mfe (e.g. lore-note-marketing-banner.svg)"
          />
          {value.source_type === 'external_api' && (
            <Select
              label="Message animation"
              value={staticContent.message_animation ?? 'none'}
              options={MARKETING_MESSAGE_ANIMATION_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
              onChange={(v) =>
                patchStatic({
                  message_animation: (v || 'none') as MarketingMessageAnimation,
                })
              }
              disabled={disabled}
              hint="Applies to every upstream (LoreNote) line. Set Typewriter on the LoreNote banner."
            />
          )}
          <Input
            label="CTA label"
            value={staticContent.cta_label ?? ''}
            onChange={(v) => patchStatic({ cta_label: v })}
            disabled={disabled}
            hint="Used only when CTA action is set. Home hides the button when action is None."
          />
          <Select
            label="CTA action"
            value={staticContent.cta_action ?? ''}
            options={MARKETING_BANNER_CTA_ACTION_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            onChange={(v) =>
              patchStatic({
                cta_action: (v || null) as MarketingBannerCtaAction | null,
              })
            }
            disabled={disabled}
          />
          {isNavigationCta && (
            <>
              <Input
                label="CTA target"
                value={staticContent.cta_target ?? ''}
                onChange={(v) => patchStatic({ cta_target: v || null })}
                disabled={disabled}
                hint="Static URL or path when not using metadata key"
              />
              <Input
                label="CTA metadata key"
                value={staticContent.cta_metadata_key ?? ''}
                onChange={(v) => patchStatic({ cta_metadata_key: v || null })}
                disabled={disabled}
                hint="Upstream JSON key (e.g. lorenote_web_url for external_browser)"
              />
            </>
          )}
          {ctaAction === 'cross_mfe' && (
            <Input
              label="CTA host (cross_mfe)"
              value={staticContent.cta_host ?? ''}
              onChange={(v) => patchStatic({ cta_host: v || null })}
              disabled={disabled}
              hint="Federated module host for cross_mfe (e.g. streaks)"
            />
          )}
          {isRedemptionCta && (
            <Input
              label="CTA reward key"
              value={staticContent.cta_reward_key ?? ''}
              onChange={(v) => patchStatic({ cta_reward_key: v || null })}
              disabled={disabled}
              required
              hint="Catalog reward_key (e.g. lore-note) — MFE resolves to reward_id"
            />
          )}
        </CardBody>
      </Card>

      {value.source_type === 'external_api' && (
        <Card>
          <CardHeader title="External API (external_api)" />
          <CardBody>
            <Input
              label="Nonprod base URL"
              value={baseUrl.nonprod ?? ''}
              onChange={(v) =>
                patchExternal({
                  base_url: { ...baseUrl, nonprod: v },
                })
              }
              disabled={disabled}
            />
            <Input
              label="Prod base URL"
              value={baseUrl.prod ?? ''}
              onChange={(v) =>
                patchExternal({
                  base_url: { ...baseUrl, prod: v },
                })
              }
              disabled={disabled}
            />
            <Input
              label="Endpoint path"
              value={externalApi.endpoint ?? ''}
              onChange={(v) => patchExternal({ endpoint: v })}
              disabled={disabled}
              required
            />
            <Select
              label="HTTP method"
              value={externalApi.method ?? 'GET'}
              options={[
                { value: 'GET', label: 'GET' },
                { value: 'POST', label: 'POST' },
              ]}
              onChange={(v) => patchExternal({ method: v })}
              disabled={disabled}
            />
            <TextArea
              label="Request headers (JSON)"
              value={headersText}
              onChange={handleHeadersChange}
              disabled={disabled}
              rows={3}
              // eslint-disable-next-line no-template-curly-in-string
              placeholder='{"Authorization": "Bearer ${jwt_token}"}'
              // eslint-disable-next-line no-template-curly-in-string
              hint="Template variables: ${jwt_token}. Required for LoreNote JWT passthrough on GET."
              error={headersError || undefined}
            />
            {httpMethod === 'POST' && (
              <TextArea
                label="Request payload (JSON)"
                value={payloadText}
                onChange={handlePayloadChange}
                disabled={disabled}
                rows={6}
                // eslint-disable-next-line no-template-curly-in-string
                placeholder='{"rewards_context": {"token": "${jwt_token}"}}'
                // eslint-disable-next-line no-template-curly-in-string
                hint="Template variables: ${jwt_token}. Sent as JSON body on POST."
                error={payloadError || undefined}
              />
            )}
          </CardBody>
        </Card>
      )}

      {value.source_type === 'static' && (
        <Card>
          <CardHeader title="Static messages" />
          <CardBody>
            <p className="marketing-banner-form__static-messages-hint">
              Rotating home-banner subtext; add multiple lines to rotate. Type catalog
              placeholders into a message, for example You must reverify for {'{{partner_dba}}'}.
            </p>
            <div className="marketing-banner-form__template-helper">
              <h5>Available template variables</h5>
              <p className="marketing-banner-form__template-helper-hint">
                Type these into message text. They apply to messages only, not title or CTA.
              </p>
              <div className="marketing-banner-form__template-list">
                {TEMPLATE_VARIABLES.map((tv) => (
                  <div key={tv.variable} className="marketing-banner-form__template-item">
                    <code className="marketing-banner-form__template-var">{tv.variable}</code>
                    <span className="marketing-banner-form__template-desc">{tv.description}</span>
                  </div>
                ))}
              </div>
            </div>
            <StaticMessagesEditor
              value={staticContent.messages ?? []}
              onChange={(messages) => patchStatic({ messages })}
              disabled={disabled}
            />
          </CardBody>
        </Card>
      )}
    </div>
  );
};
